import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { QrCode, Loader2, Scan } from 'lucide-react';

const ScannerLogin = () => {
  const [campaignId, setCampaignId] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginScanner } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await loginScanner(campaignId.toUpperCase(), username, password);
      toast.success('Login successful');
      navigate('/scanner/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-700 bg-slate-800/50 backdrop-blur">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
            <Scan className="h-8 w-8 text-white" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-white">Scanner Login</CardTitle>
            <CardDescription className="text-slate-400">
              Enter your credentials to access the scanner
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="campaignId" className="text-slate-300">Campaign ID</Label>
              <Input
                id="campaignId"
                placeholder="e.g., TECH2026"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value.toUpperCase())}
                required
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 uppercase"
                data-testid="campaign-id-input"
              />
              <p className="text-xs text-slate-500">Ask your event admin for the Campaign ID</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-300">Username</Label>
              <Input
                id="username"
                placeholder="Your scanner username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="username-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                <>
                  <QrCode className="mr-2 h-4 w-4" />
                  Access Scanner
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-6 pt-6 border-t border-slate-700">
            <p className="text-xs text-center text-slate-500">
              This is a scanner-only portal. For admin access, please use the{' '}
              <a href="/admin/login" className="text-emerald-400 hover:underline">Admin Portal</a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScannerLogin;
